<template>
  <DefaultLayout>
    <div class="px-responsive py-responsive mx-auto">
      <FormContainer
        :title="productType + ' Quote'"
        :is-loading="isSubmitting"
        loading-message="Submitting your quote request..."
        :error-message="formError"
        :success-message="successMessage"
        :submit-label="isSuccess ? 'Submit Another Quote' : 'Submit'"
        @submit="submitForm"
      >
        <!-- Product icon -->
        <div class="flex justify-center mb-responsive">
          <img :src="imagePath" class="w-16 sm:w-20 md:w-24" alt="Insurance Type" />
        </div>
        
        <!-- Form content -->
        <div v-if="!isSuccess">
          <!-- Personal Information Section -->
          <div class="form-section">
            <h3 class="text-responsive-base font-medium text-gray-700 mb-responsive">Personal Information</h3>
            
            <FormField
              field-id="name"
              label="First Name"
              :error="errors.name"
              required
            >
              <input
                id="name"
                v-model="formData.name"
                type="text"
                placeholder="Your First Name"
                class="input-responsive"
                :class="{ 'border-red-300 bg-red-50': !!errors.name }"
                @blur="validateField('name')"
              />
            </FormField>
            
            <FormField
              field-id="lastName"
              label="Last Name"
              :error="errors.lastName"
              required
            >
              <input
                id="lastName"
                v-model="formData.lastName"
                type="text"
                placeholder="Your Last Name"
                class="input-responsive"
                :class="{ 'border-red-300 bg-red-50': !!errors.lastName }"
                @blur="validateField('lastName')"
              />
            </FormField>
            
            <FormField
              field-id="email"
              label="Email Address"
              :error="errors.email"
              required
            >
              <input
                id="email"
                v-model="formData.email"
                type="email"
                placeholder="your.email@example.com"
                class="input-responsive"
                :class="{ 'border-red-300 bg-red-50': !!errors.email }"
                @blur="validateField('email')"
              />
            </FormField>
            
            <FormField
              field-id="phone"
              label="Phone Number"
              :error="errors.phone"
              required
            >
              <input
                id="phone"
                v-model="formData.phone"
                type="tel"
                placeholder="(555) 123-4567"
                class="input-responsive"
                :class="{ 'border-red-300 bg-red-50': !!errors.phone }"
                @blur="validateField('phone')"
              />
            </FormField>
          </div>
          
          <!-- Property Information Section (conditionally shown) -->
          <div v-if="showPropertySection" class="form-section">
            <h3 class="text-responsive-base font-medium text-gray-700 mb-responsive">Property Information</h3>
            
            <FormField
              field-id="propertyAddress"
              label="Property Address"
              :error="errors.propertyAddress"
              required
            >
              <input
                id="propertyAddress"
                v-model="formData.propertyAddress"
                type="text"
                placeholder="123 Main Street, City, State ZIP"
                class="input-responsive"
                :class="{ 'border-red-300 bg-red-50': !!errors.propertyAddress }"
                @blur="validateField('propertyAddress')"
              />
            </FormField>
          </div>
          
          <!-- Additional Information Section -->
          <div class="form-section">
            <h3 class="text-responsive-base font-medium text-gray-700 mb-responsive">Additional Information</h3>
            
            <FormField
              field-id="message"
              label="Your Message"
            >
              <textarea
                id="message"
                v-model="formData.message"
                rows="4"
                placeholder="Tell us about your insurance needs..."
                class="input-responsive"
              ></textarea>
            </FormField>
            
            <div class="form-field">
              <label class="block text-responsive-sm font-medium text-gray-700 mb-2">
                Are you currently insured with Insurance Express and Member Companies?
                <span class="text-red-500">*</span>
                <span class="help-icon">
                  <span class="question-mark">?</span>
                  <span class="help-content">
                    <span class="text-xs text-blue-500 font-bold" style="color: #0b2779">
                      Member Companies<br />
                      <span class="text-xs text-gray-500">
                        GLV Insurance<br />
                        K&G Insurance<br />
                        QuoteSlash<br />
                        RocketFlood<br />
                        RocketMGA<br />
                        Nubuild<br />
                        TheLeadExchange<br />
                        Protego VIP<br />
                        Str8line Group<br />
                        Str8line Enterprises
                      </span>
                    </span>
                  </span>
                </span>
              </label>
              
              <div class="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0" :class="{ 'text-red-500': !!errors.insured }">
                <div class="flex items-center">
                  <input
                    id="insuredYes"
                    v-model="formData.insured"
                    type="radio"
                    value="Yes"
                    class="h-4 w-4 text-blue-600"
                  />
                  <label for="insuredYes" class="ml-2 text-responsive-sm">Yes</label>
                </div>
                <div class="flex items-center">
                  <input
                    id="insuredNo"
                    v-model="formData.insured"
                    type="radio"
                    value="No"
                    class="h-4 w-4 text-blue-600"
                  />
                  <label for="insuredNo" class="ml-2 text-responsive-sm">No</label>
                </div>
              </div>
              
              <p v-if="errors.insured" class="mt-1 text-responsive-xs text-red-500">
                {{ errors.insured }}
              </p>
            </div>
          </div>
          
          <p class="text-responsive-xs text-gray-500">
            Fields marked with <span class="text-red-500">*</span> are required.
          </p>
        </div>
        
        <!-- Success view -->
        <div v-else class="text-center py-responsive">
          <div class="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckIcon class="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
          </div>
          <h3 class="text-responsive-lg font-medium text-gray-800 mb-2">Quote Request Submitted!</h3>
          <p class="text-responsive-sm text-gray-600 mb-responsive">
            Thank you for your interest. One of our agents will contact you shortly.
          </p>
        </div>
      </FormContainer>
    </div>
  </DefaultLayout>
</template>

<script>
import { ref, computed, watch } from 'vue';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import FormContainer from '@/components/common/FormContainer.vue';
import FormField from '@/components/common/FormField.vue';
import CheckIcon from '@/components/icons/CheckIcon.vue';
import { useNotifications } from '@/composables/ui/useNotifications';
import { validateForm, validationSchemas, isNotEmpty } from '@/utils/formValidation';
import axios from 'axios';

// Import product icons
const productIcons = import.meta.glob('@/assets/images/products/icons/**/*.svg', { eager: true });

export default {
  name: 'StandardQuoteForm',
  
  components: {
    DefaultLayout,
    FormContainer,
    FormField,
    CheckIcon
  },
  
  props: {
    productType: {
      type: String,
      default: 'Home Insurance'
    },
    campaign: {
      type: String,
      default: ''
    },
    utm_source: {
      type: String,
      default: ''
    },
    utm_medium: {
      type: String,
      default: ''
    },
    utm_campaign: {
      type: String,
      default: ''
    },
    utm_content: {
      type: String,
      default: ''
    },
    utm_term: {
      type: String,
      default: ''
    }
  },
  
  setup(props) {
    // Form data
    const formData = ref({
      name: '',
      lastName: '',
      email: '',
      phone: '',
      propertyAddress: '',
      message: '',
      insured: '',
      productType: props.productType,
      // Campaign tracking
      campaign: props.campaign,
      utm_source: props.utm_source,
      utm_medium: props.utm_medium,
      utm_campaign: props.utm_campaign,
      utm_content: props.utm_content,
      utm_term: props.utm_term
    });
    
    // Form state
    const isSubmitting = ref(false);
    const isSuccess = ref(false);
    const successMessage = ref('');
    const formError = ref('');
    const errors = ref({});
    const { showNotification } = useNotifications();
    
    // Capture campaign parameters from URL (merge with props)
    const captureUrlParameters = () => {
      const urlParams = new URLSearchParams(window.location.search);
      
      // Merge router props with URL parameters (props take precedence)
      formData.value.campaign = formData.value.campaign || urlParams.get('campaign') || '';
      formData.value.utm_source = formData.value.utm_source || urlParams.get('utm_source') || '';
      formData.value.utm_medium = formData.value.utm_medium || urlParams.get('utm_medium') || '';
      formData.value.utm_campaign = formData.value.utm_campaign || urlParams.get('utm_campaign') || '';
      formData.value.utm_content = formData.value.utm_content || urlParams.get('utm_content') || '';
      formData.value.utm_term = formData.value.utm_term || urlParams.get('utm_term') || '';
      
      // Log for debugging
      if (formData.value.campaign) {
        console.log('Campaign detected:', formData.value.campaign);
        console.log('UTM parameters:', {
          source: formData.value.utm_source,
          medium: formData.value.utm_medium,
          campaign: formData.value.utm_campaign
        });
      }
    };

    // Initialize campaign capture on component mount
    captureUrlParameters();

    // Update product type when props change
    watch(() => props.productType, (newValue) => {
      formData.value.productType = newValue;
    });
    
    // Image path based on product type
    const imagePath = computed(() => {
      const productTypeString = formData.value.productType || ' '; 
      const firstWord = productTypeString.split(' ')[0].toLowerCase();
      // Construct the path to match the keys from import.meta.glob
      const iconPath = `/resources/js/assets/images/products/icons/${firstWord}/icon-${firstWord}-main.svg`;
      
      if (productIcons[iconPath]) {
        return productIcons[iconPath].default;
      }
      
      // Fallback with the correct prefix
      const defaultIconPath = '/resources/js/assets/images/products/icons/default-icon.svg';
      return productIcons[defaultIconPath]?.default || '';
    });
    
    // Show property section for certain product types
    const showPropertySection = computed(() => {
      const productType = formData.value.productType;
      return ['Home Insurance', 'Auto Insurance', 'Flood Insurance', 'Umbrella Insurance'].includes(productType);
    });
    
    // Validate a single field
    const validateField = (field) => {
      const schema = validationSchemas.standardQuote;
      
      if (schema[field]) {
        const { validate, message } = schema[field];
        const isValid = validate(formData.value[field]);
        
        if (!isValid) {
          errors.value[field] = message;
        } else {
          delete errors.value[field];
        }
        
        return isValid;
      }
      
      return true;
    };
    
    // Validate all fields
    const validateAll = () => {
      const { errors: validationErrors, isValid } = validateForm(formData.value, validationSchemas.standardQuote);
      errors.value = validationErrors;
      return isValid;
    };
    
    // Submit the form
    const submitForm = async () => {
      // Prevent multiple submissions
      if (isSubmitting.value) {
        console.log('Submission already in progress, ignoring duplicate request');
        return;
      }

      // Reset form state
      isSubmitting.value = true;
      formError.value = '';
      
      try {
        // Validate all fields
        if (!validateAll()) {
          formError.value = 'Please correct the errors and try again.';
          return;
        }

        // Add submission timestamp to help identify duplicates
        const submissionData = {
          ...formData.value,
          submissionTimestamp: Date.now(),
          userAgent: navigator.userAgent
        };
        
        // Submit to API
        const response = await axios.post('/api/submit-quote', submissionData);
        
        if (response.data.success) {
          // Show success message
          isSuccess.value = true;
          successMessage.value = response.data.message || 'Quote request submitted successfully!';
          
          // Reset form data but preserve campaign info
          const campaignData = {
            campaign: formData.value.campaign,
            utm_source: formData.value.utm_source,
            utm_medium: formData.value.utm_medium,
            utm_campaign: formData.value.utm_campaign,
            utm_content: formData.value.utm_content,
            utm_term: formData.value.utm_term
          };
          
          formData.value = {
            name: '',
            lastName: '',
            email: '',
            phone: '',
            propertyAddress: '',
            message: '',
            insured: '',
            productType: props.productType,
            // Preserve campaign tracking
            ...campaignData
          };
          errors.value = {};
        } else {
          // Handle API error
          formError.value = response.data.message || 'An error occurred submitting your quote request.';
        }
      } catch (error) {
        // Handle validation errors
        if (error.response && error.response.status === 422) {
          const validationErrors = error.response.data.errors || {};
          
          errors.value = {
            ...errors.value,
            ...validationErrors
          };
          
          formError.value = error.response.data.message || 'Please correct the errors and try again.';
        } else if (error.response && error.response.status === 429) {
          // Handle rate limiting
          formError.value = 'You are submitting requests too quickly. Please wait a moment and try again.';
        } else {
          // Handle other errors
          formError.value = 'An unexpected error occurred. Please try again later.';
          showNotification({ title: 'Submission Error', message: 'An unexpected error occurred.', type: 'error' });
        }
      } finally {
        // Add a small delay before allowing another submission to prevent rapid clicking
        setTimeout(() => {
          isSubmitting.value = false;
        }, 1000);
      }
    };
    
    return {
      formData,
      isSubmitting,
      isSuccess,
      successMessage,
      formError,
      errors,
      imagePath,
      showPropertySection,
      validateField,
      validateAll,
      submitForm,
      showNotification
    };
  }
};
</script>

<style scoped>
.help-icon {
  @apply relative inline-block ml-1 text-gray-500;
}

.question-mark {
  @apply inline-flex items-center justify-center w-4 h-4 rounded-full text-xs border border-gray-400;
  cursor: help;
}

.help-content {
  @apply absolute z-10 hidden p-2 w-48 text-xs bg-white border rounded shadow-lg -right-1;
  top: 100%;
}

.help-icon:hover .help-content {
  @apply block;
}
</style> 